import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { format } from "date-fns";
import { useState } from "react";
import { Navigate, NavLink, useNavigate, useParams } from "react-router-dom";

import AppointmentCard from "@/components/AppointmentCard";
import { useAuth } from "@/components/Auth";
import Button from "@/components/Button";
import DownloadAppCTA from "@/components/DownloadAppCTA";
import ErrorText from "@/components/ErrorText";
import Header from "@/components/Header";
import Link from "@/components/Link";
import Loader from "@/components/Loader";
import Modal from "@/components/Modal";
import { useNotification } from "@/components/Notification";
import StoreCard from "@/components/StoreCard";
import WaiverCTA from "@/components/WaiverCTA";
import { ExternalLinks } from "@/constants/links";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { setAppointment, useAppointmentAndStore } from "@/hooks/appointments";
import { useDashboardQuery } from "@/hooks/booking";
import { usePageEventTracking } from "@/hooks/event-tracking";
import {
  ConfirmAppointmentResult,
  confirmAppointmentsForDate,
  confirmSingleAppointment,
} from "@/services/booking";

export default function AppointmentDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { waiver } = useAuth();
  const queryClient = useQueryClient();
  const appointmentQuery = useAppointmentAndStore(id);

  const _appointment =
    appointmentQuery.type === "success" ? appointmentQuery.appointment : null;

  const notify = useNotification();

  const dashboardQuery = useDashboardQuery();

  const [
    showRescheduleLateCancellationConfirmationModal,
    setRescheduleLateCancellationConfirmationModal,
  ] = useState(false);
  const [
    showConfirmSameDayAppointmentsModal,
    setShowConfirmSameDayAppointmentsModal,
  ] = useState(false);

  const confirmSingleMutation = useMutation({
    mutationFn: confirmSingleAppointment,
  });

  usePageEventTracking({
    service_name: _appointment?.service_description,
    service_date: _appointment?.start_datetime,
  });

  useDocumentTitle(
    PageTitle.APPOINTMENT(_appointment?.service_description ?? ""),
  );

  function handleWaiverCTAClick() {
    navigate(PageRoute.ACCOUNT_PROFILE);
  }

  if (!id) {
    return <Navigate to={PageRoute.HOME} />;
  }

  if (appointmentQuery.type === "pending" || dashboardQuery.isPending) {
    return <Loader />;
  }

  if (appointmentQuery.type === "error" || dashboardQuery.isError) {
    return <ErrorText error={ErrorMessages.GENERIC} center />;
  }

  if (appointmentQuery.type === "notCustomersAppointment") {
    return <ErrorText error={ErrorMessages.INCORRECT_ACCOUNT} center />;
  }

  const { appointment, store } = appointmentQuery;

  const onRescheduleClicked = () => {
    if (appointment.warnBeforeReschedule) {
      setRescheduleLateCancellationConfirmationModal(true);
    } else {
      navigate(`${PageRoute.APPOINTMENT_RESCHEDULE}?appointment=${id}`);
    }
  };

  const onConfirmResult = (res: ConfirmAppointmentResult) => {
    if (res.type === "error") {
      notify(ErrorMessages.GENERIC, "error");
      return;
    }
    for (const appointment of res.appointments) {
      setAppointment(appointment, queryClient);
    }
    notify("Appointment successfully confirmed", "success");
  };

  const onConfirm = () => {
    const hasMultipleToConfirmForSameDay =
      dashboardQuery.data.upcoming.filter(
        (x) => x.canConfirm && x.date === appointment.date,
      ).length > 1;
    if (hasMultipleToConfirmForSameDay) {
      setShowConfirmSameDayAppointmentsModal(true);
    } else {
      confirmSingleMutation.mutate(
        { appointmentId: appointment.id },
        {
          onSuccess: onConfirmResult,
        },
      );
    }
  };

  return (
    <>
      <Header className="mb-6">Your upcoming appointment</Header>

      <AppointmentCard appointment={appointment} />

      {appointment.canConfirm ? (
        <div
          className={
            "mb-6 mt-4 flex w-full flex-col items-center justify-center"
          }
        >
          {confirmSingleMutation.isPending ? (
            <>
              <Loader />
            </>
          ) : (
            <Button
              variant={"secondary"}
              onClick={onConfirm}
              className={"w-full"}
            >
              Confirm appointment
            </Button>
          )}
        </div>
      ) : appointment.isConfirmed ? (
        <div
          className={"my-6 flex w-full flex-col items-center justify-center"}
        >
          <span className={"font-semibold text-amalfi-coast"}>
            Appointment is confirmed
          </span>
        </div>
      ) : (
        <div className={"my-7"}></div>
      )}

      {store && <StoreCard className="mb-8" store={store} />}

      <p className="mb-4">
        Please arrive at least 10 minutes before your scheduled appointment to
        ensure that you have enough time to prepare for your service.
      </p>

      <p className="mb-6">
        Check out our{" "}
        <Link href={ExternalLinks.FAQS} target="_blank">
          FAQs for more frequently asked questions and answers
        </Link>{" "}
        around what to expect from your visit.
      </p>

      <div className="mb-4 flex flex-col gap-4">
        {waiver.state === "NOT_SIGNED" && (
          <WaiverCTA onClick={handleWaiverCTAClick} />
        )}

        {waiver.state === "EXPIRED" && (
          <WaiverCTA
            expiresAt={waiver.expiresAt}
            onClick={handleWaiverCTAClick}
          />
        )}

        <Button
          as={NavLink}
          to={{
            pathname: PageRoute.APPOINTMENT_BOOK,
            search: `service=${appointment.service_id}`,
          }}
        >
          Book this service again
        </Button>

        {appointment.canReschedule ? (
          <>
            <Button variant={"secondary"} onClick={onRescheduleClicked}>
              Reschedule appointment
            </Button>
          </>
        ) : appointment.warnBeforeReschedule ? (
          <>
            {/* if cannot reschedule but warn before reschedule, show late cancellation message */}
            <div className={"flex flex-col justify-center"}>
              <span className={"text-center font-sans"}>
                Rescheduling is unavailable due to our{" "}
                <Link href={ExternalLinks.TERMS} target="_blank">
                  late cancellation policy
                </Link>
                .
              </span>
            </div>
          </>
        ) : null}

        {appointment.canCancel ? (
          <Button
            as={NavLink}
            to={PageRoute.APPOINTMENT_CANCEL(id ?? "")}
            variant="tertiary"
            color="red"
          >
            Cancel appointment
          </Button>
        ) : null}
      </div>

      <DownloadAppCTA />

      <ConfirmSameDayAppointmentsModal
        setShowConfirmSameDayAppointmentsModal={
          setShowConfirmSameDayAppointmentsModal
        }
        showConfirmSameDayAppointmentsModal={
          showConfirmSameDayAppointmentsModal
        }
        date={appointment.date}
        startDateTimeTZ={appointment.start_datetime_tz}
        id={appointment.id}
        onConfirmResult={onConfirmResult}
      />

      <RescheduleModal
        showRescheduleLateCancellationConfirmationModal={
          showRescheduleLateCancellationConfirmationModal
        }
        setRescheduleLateCancellationConfirmationModal={
          setRescheduleLateCancellationConfirmationModal
        }
        id={appointment.id}
      />
    </>
  );
}

function RescheduleModal(props: {
  showRescheduleLateCancellationConfirmationModal: boolean;
  setRescheduleLateCancellationConfirmationModal: (value: boolean) => void;
  id: number;
}) {
  const {
    showRescheduleLateCancellationConfirmationModal,
    setRescheduleLateCancellationConfirmationModal,
  } = props;
  return (
    <Modal
      open={showRescheduleLateCancellationConfirmationModal}
      onClose={() => {
        setRescheduleLateCancellationConfirmationModal(false);
      }}
    >
      <div className={"flex max-w-[500px] flex-col"}>
        <Header className="mb-4 text-error-message">
          Are you sure you want to reschedule?
        </Header>

        <p className={"mb-4 font-sans"}>
          Please note that rescheduling your appointment with less than 24{" "}
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          hours' notice may result in a late rescheduling fee.
        </p>

        <div className="mb-4 flex flex-row justify-between gap-4">
          <Button
            as={NavLink}
            to={{
              pathname: PageRoute.APPOINTMENT_RESCHEDULE,
              search: `appointment=${props.id}`,
            }}
            variant="primary"
            color={"red"}
          >
            Confirm reschedule
          </Button>
          <Button
            onClick={() => {
              setRescheduleLateCancellationConfirmationModal(false);
            }}
            variant="secondary"
            className={"px-10"}
          >
            Keep
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ConfirmSameDayAppointmentsModal(props: {
  showConfirmSameDayAppointmentsModal: boolean;
  setShowConfirmSameDayAppointmentsModal: (value: boolean) => void;
  id: number;
  date: string;
  startDateTimeTZ: string;
  onConfirmResult: (res: ConfirmAppointmentResult) => void;
}) {
  const {
    showConfirmSameDayAppointmentsModal,
    setShowConfirmSameDayAppointmentsModal,
  } = props;

  const formattedDate = format(new Date(props.startDateTimeTZ), "MM/dd/yyyy");

  const onConfirmResult = (res: ConfirmAppointmentResult) => {
    props.onConfirmResult(res);
    setShowConfirmSameDayAppointmentsModal(false);
  };

  const confirmSingleMutation = useMutation({
    mutationFn: confirmSingleAppointment,
  });

  const confirmSameSayAppointmentsMutation = useMutation({
    mutationFn: confirmAppointmentsForDate,
  });

  const onConfirmSingle = () => {
    confirmSingleMutation.mutate(
      { appointmentId: props.id },
      {
        onSuccess: onConfirmResult,
      },
    );
  };

  const onConfirmSameDay = () => {
    confirmSameSayAppointmentsMutation.mutate(
      { date: props.date },
      {
        onSuccess: onConfirmResult,
      },
    );
  };

  return (
    <Modal
      open={showConfirmSameDayAppointmentsModal}
      onClose={() => {
        setShowConfirmSameDayAppointmentsModal(false);
      }}
    >
      <div className={"flex max-w-[500px] flex-col"}>
        <Header className="mb-6">Confirm all appointments?</Header>

        <p className={"mb-4 font-sans"}>
          You have multiple upcoming appointments on {formattedDate}. Would you
          like to confirm all upcoming appointments for this date?
        </p>

        <div className="mb-6 flex flex-col">
          <div className={"mb-6 w-full"}>
            {confirmSingleMutation.isPending ? (
              <>
                <Loader />
              </>
            ) : (
              <Button
                variant="primary"
                className={"w-full"}
                onClick={onConfirmSingle}
              >
                Confirm only this one
              </Button>
            )}
          </div>
          <div className={"flex w-full flex-col"} onClick={onConfirmSameDay}>
            {confirmSameSayAppointmentsMutation.isPending ? (
              <>
                <Loader />
              </>
            ) : (
              <Button variant="secondary" className={"w-full"}>
                Confirm all
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
